/**
 * Menu Styling Utilities
 * 
 * This file provides utility functions to generate consistent CSS classes
 * and inline styles based on menu customization settings.
 */

import { cn } from '@/lib/utils';
import type {
  BorderRadius,
  Shadow,
  BorderWidth,
  SpacingScale,
  FontScale,
  FontWeight,
  MenuCustomization,
} from '@/types/menuCustomization';

// ============================================================================
// Border Radius Utilities
// ============================================================================

export function getBorderRadiusClass(radius: BorderRadius): string {
  const radiusMap: Record<BorderRadius, string> = {
    none: 'rounded-none',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    '2xl': 'rounded-2xl',
    full: 'rounded-full',
  };
  
  return radiusMap[radius];
}

// ============================================================================
// Shadow Utilities
// ============================================================================

export function getShadowClass(shadow: Shadow): string {
  const shadowMap: Record<Shadow, string> = {
    none: 'shadow-none',
    sm: 'shadow-sm',
    md: 'shadow-md',
    lg: 'shadow-lg',
    xl: 'shadow-xl',
    '2xl': 'shadow-2xl',
  };
  
  return shadowMap[shadow];
}

// ============================================================================
// Border Utilities
// ============================================================================

export function getBorderClass(width: BorderWidth, color?: string): string {
  if (width === 0) return '';
  
  const borderWidthMap: Record<BorderWidth, string> = {
    0: '',
    1: 'border',
    2: 'border-2',
    4: 'border-4',
  };
  
  return borderWidthMap[width];
}

export function getBorderStyle(width: BorderWidth, color: string): React.CSSProperties {
  if (width === 0) return {};
  return { borderColor: color };
}

// ============================================================================
// Spacing Utilities
// ============================================================================

export function getSpacingClass(scale: SpacingScale, type: 'gap' | 'padding' | 'margin'): string {
  const spacingValues: Record<SpacingScale, { gap: string; padding: string; margin: string }> = {
    compact: {
      gap: 'gap-2',
      padding: 'p-2',
      margin: 'm-2',
    },
    normal: {
      gap: 'gap-4',
      padding: 'p-4',
      margin: 'm-4',
    },
    relaxed: {
      gap: 'gap-6',
      padding: 'p-6',
      margin: 'm-6',
    },
    loose: {
      gap: 'gap-8',
      padding: 'p-8',
      margin: 'm-8',
    },
  };
  
  return spacingValues[scale][type];
}

// ============================================================================
// Typography Utilities
// ============================================================================

export function getFontScaleClasses(scale: FontScale, element: 'heading' | 'body' | 'caption'): string {
  const scaleMap: Record<FontScale, { heading: string; body: string; caption: string }> = {
    xs: {
      heading: 'text-base',
      body: 'text-xs',
      caption: 'text-[10px]',
    },
    sm: {
      heading: 'text-lg',
      body: 'text-sm',
      caption: 'text-xs',
    },
    md: {
      heading: 'text-xl',
      body: 'text-base',
      caption: 'text-sm',
    },
    lg: {
      heading: 'text-2xl',
      body: 'text-lg',
      caption: 'text-base',
    },
    xl: {
      heading: 'text-3xl',
      body: 'text-xl',
      caption: 'text-lg',
    },
  };
  
  return scaleMap[scale][element];
}

export function getFontWeightClass(weight: FontWeight): string {
  const weightMap: Record<FontWeight, string> = {
    light: 'font-light',
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
    extrabold: 'font-extrabold',
  };
  
  return weightMap[weight];
}

export function getFontFamilyClass(family: string): string {
  const familyMap: Record<string, string> = {
    inter: 'font-sans',
    roboto: 'font-roboto',
    poppins: 'font-poppins',
    playfair: 'font-serif',
    'noto-sans-arabic': 'font-noto-sans-arabic',
  };
  
  return familyMap[family] || 'font-sans';
}

// ============================================================================
// Composite Style Generators
// ============================================================================

/**
 * Generate card container classes based on customization
 */
export function getCardContainerClasses(customization: MenuCustomization): string {
  return cn(
    'bg-white transition-all',
    getBorderRadiusClass(customization.effects.borderRadius),
    getShadowClass(customization.effects.shadow),
    getSpacingClass(customization.spacing.cardPadding, 'padding'),
    getBorderClass(customization.effects.borderWidth),
    customization.effects.hoverEffect && 'hover:shadow-lg hover:scale-[1.02]',
    customization.effects.transitionSpeed === 'fast' && 'duration-150',
    customization.effects.transitionSpeed === 'normal' && 'duration-300',
    customization.effects.transitionSpeed === 'slow' && 'duration-500',
    customization.effects.glassMorphism && 'backdrop-blur-md bg-opacity-80',
  );
}

/**
 * Generate inline styles for card (for color customizations)
 */
export function getCardContainerStyles(customization: MenuCustomization): React.CSSProperties {
  return {
    backgroundColor: customization.colors.surface,
    ...(customization.effects.borderWidth > 0 && {
      borderColor: customization.colors.border,
    }),
  };
}

/**
 * Generate title/heading classes
 */
export function getHeadingClasses(customization: MenuCustomization, level: 'h1' | 'h2' | 'h3' = 'h2'): string {
  const sizeClass = level === 'h1' 
    ? getFontScaleClasses(customization.typography.scale, 'heading')
    : level === 'h2'
    ? getFontScaleClasses(customization.typography.scale, 'body')
    : getFontScaleClasses(customization.typography.scale, 'caption');
    
  return cn(
    sizeClass,
    getFontWeightClass(customization.typography.headingWeight),
    getFontFamilyClass(customization.typography.fontFamily),
    customization.typography.textShadow && 'text-shadow',
  );
}

/**
 * Generate body text classes
 */
export function getBodyTextClasses(customization: MenuCustomization, secondary = false): string {
  return cn(
    getFontScaleClasses(customization.typography.scale, 'body'),
    getFontWeightClass(customization.typography.bodyWeight),
    getFontFamilyClass(customization.typography.fontFamily),
    secondary ? 'text-gray-500' : '',
  );
}

/**
 * Generate inline styles for text (for color customizations)
 */
export function getTextStyles(customization: MenuCustomization, secondary = false): React.CSSProperties {
  return {
    color: secondary ? customization.colors.textSecondary : customization.colors.text,
  };
}

/**
 * Generate grid/layout classes
 */
export function getLayoutGridClasses(customization: MenuCustomization): string {
  const { layout, spacing } = customization;
  
  if (layout.type === 'list') {
    return cn('flex flex-col', getSpacingClass(spacing.cardGap, 'gap'));
  }
  
  if (layout.type === 'grid' || layout.type === 'photo-grid') {
    return cn(
      'grid',
      getSpacingClass(spacing.cardGap, 'gap'),
      `grid-cols-${layout.gridColumns?.mobile || 1}`,
      `sm:grid-cols-${layout.gridColumns?.tablet || 2}`,
      `lg:grid-cols-${layout.gridColumns?.desktop || 3}`,
    );
  }
  
  if (layout.type === 'masonry') {
    return cn('masonry-grid', getSpacingClass(spacing.cardGap, 'gap'));
  }
  
  return cn('flex flex-col', getSpacingClass(spacing.cardGap, 'gap'));
}

/**
 * Generate background pattern CSS
 */
export function getBackgroundPatternStyles(pattern: 'dots' | 'grid' | 'stripes' | 'waves' | 'geometric', color: string): React.CSSProperties {
  const baseStyles: React.CSSProperties = {
    backgroundRepeat: 'repeat',
    backgroundSize: pattern === 'dots' ? '20px 20px' : pattern === 'grid' ? '40px 40px' : '60px 60px',
  };

  switch (pattern) {
    case 'dots':
      baseStyles.backgroundImage = `radial-gradient(circle, ${color} 1px, transparent 1px)`;
      break;
    case 'grid':
      baseStyles.backgroundImage = `linear-gradient(${color} 1px, transparent 1px), linear-gradient(90deg, ${color} 1px, transparent 1px)`;
      break;
    case 'stripes':
      baseStyles.backgroundImage = `repeating-linear-gradient(45deg, ${color}, ${color} 10px, transparent 10px, transparent 20px)`;
      break;
    case 'waves':
      baseStyles.backgroundImage = `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 30 Q 15 20, 30 30 T 60 30' stroke='${encodeURIComponent(color)}' stroke-width='2' fill='none'/%3E%3C/svg%3E")`;
      break;
    case 'geometric':
      baseStyles.backgroundImage = `linear-gradient(135deg, ${color}22 25%, transparent 25%), linear-gradient(225deg, ${color}22 25%, transparent 25%), linear-gradient(45deg, ${color}22 25%, transparent 25%), linear-gradient(315deg, ${color}22 25%, transparent 25%)`;
      baseStyles.backgroundSize = '40px 40px';
      baseStyles.backgroundPosition = '0 0, 20px 0, 20px -20px, 0px 20px';
      break;
  }

  return baseStyles;
}

/**
 * Generate background styles
 */
export function getBackgroundStyles(customization: MenuCustomization): React.CSSProperties {
  const { background } = customization;
  
  const baseStyles: React.CSSProperties = {};
  
  if (background.type === 'none') {
    return baseStyles;
  }
  
  if (background.type === 'color') {
    baseStyles.backgroundColor = background.value;
  }
  
  if (background.type === 'gradient' && background.gradient) {
    const { type, angle, colors } = background.gradient;
    if (type === 'linear') {
      baseStyles.backgroundImage = `linear-gradient(${angle || 180}deg, ${colors.join(', ')})`;
    } else {
      baseStyles.backgroundImage = `radial-gradient(circle, ${colors.join(', ')})`;
    }
  }
  
  if (background.type === 'image') {
    baseStyles.backgroundImage = `url(${background.value})`;
    baseStyles.backgroundSize = 'cover';
    baseStyles.backgroundPosition = 'center';
    baseStyles.backgroundAttachment = background.attachment;
  }

  if (background.type === 'pattern' && background.pattern) {
    const patternStyles = getBackgroundPatternStyles(background.pattern.type, background.pattern.color);
    Object.assign(baseStyles, patternStyles);
  }
  
  if (background.blur) {
    baseStyles.backdropFilter = 'blur(10px)';
  }
  
  return baseStyles;
}

/**
 * Generate overlay styles for backgrounds
 */
export function getBackgroundOverlayStyles(customization: MenuCustomization): React.CSSProperties | null {
  const { background } = customization;
  
  if (background.overlayOpacity > 0) {
    return {
      position: 'absolute',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, ' + (background.overlayOpacity / 100) + ')',
      pointerEvents: 'none',
    } as React.CSSProperties;
  }
  
  return null;
}

/**
 * Generate button/CTA classes with primary color
 */
export function getPrimaryButtonClasses(customization: MenuCustomization): string {
  return cn(
    'px-4 py-2 text-white font-medium transition-all',
    getBorderRadiusClass(customization.effects.borderRadius),
    customization.effects.hoverEffect && 'hover:opacity-90 hover:scale-105',
  );
}

export function getPrimaryButtonStyles(customization: MenuCustomization): React.CSSProperties {
  return {
    backgroundColor: customization.colors.primary,
  };
}

/**
 * Generate container max-width class
 */
export function getContainerMaxWidthClass(customization: MenuCustomization): string {
  const maxWidthMap: Record<string, string> = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-3xl',
    xl: 'max-w-5xl',
    '2xl': 'max-w-7xl',
    full: 'max-w-full',
  };
  
  return maxWidthMap[customization.layout.maxWidth] || 'max-w-3xl';
}

/**
 * Generate image aspect ratio class
 */
export function getImageAspectRatioClass(customization: MenuCustomization): string {
  const ratioMap: Record<string, string> = {
    square: 'aspect-square',
    '4:3': 'aspect-[4/3]',
    '16:9': 'aspect-video',
    '3:2': 'aspect-[3/2]',
    auto: '',
  };
  
  return ratioMap[customization.layout.imageAspectRatio] || '';
}
